/**
 * @file: useRoomDetail.ts
 *
 * Loads + holds the room-detail state: the room (real or a synthesized
 * "phantom" for a not-yet-created private chat), the mute/pin toggles, and
 * the "add members" action that decides between creating a group (phantom /
 * private) and bulk-inviting (existing group). Refetches on focus so a
 * return from the contact picker reflects new members.
 */

import { ChatConversationHandle } from '@euler/app/flows/chat/lib/conversation/handle';
import { useChatSession } from '@euler/app/flows/chat/lib/session/context';
import { ChatContactPickerParams } from '@euler/app/flows/chat/lib/types';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import { usePrompt } from '@euler/functions/ux/usePrompt';
import { ChatRoomActiveMember, ChatRoomDetail } from '@euler/model/chat';
import { IdType } from '@euler/model/common';
import { useServiceFactory } from '@euler/services/factory';
import { idEquals } from '@euler/utils';
import { useFocusEffect } from '@react-navigation/native';
import { useCallback, useMemo, useRef, useState } from 'react';

/**
 * Member grid display order: the bot (服务助手) first → owner → other humans by
 * join order (`joinedAt`, ULID `id` as tiebreak).
 */
function sortRoomMembers(
  members: ChatRoomActiveMember[],
  room: ChatRoomDetail,
): ChatRoomActiveMember[] {
  const isOwner = (m: ChatRoomActiveMember) =>
    room.ownerUserId != null &&
    idEquals(m.platformUserId, room.ownerUserId) &&
    m.appId === room.ownerAppId &&
    m.appScope === room.ownerAppScope;
  const rank = (m: ChatRoomActiveMember) =>
    m.type === 'bot' ? 0 : isOwner(m) ? 1 : 2;
  return [...members].sort((a, b) => {
    const byRank = rank(a) - rank(b);
    if (byRank !== 0) return byRank;
    return (
      (a.joinedAt || '').localeCompare(b.joinedAt || '') ||
      a.id.localeCompare(b.id)
    );
  });
}

export interface RoomDetail {
  room: ChatRoomDetail | null;
  loading: boolean;
  isPhantom: boolean;
  /** A real group room: leave/dismiss apply (not private/phantom). */
  isGroup: boolean;
  /** Mute/pin can be persisted — a thread row exists for this viewer. */
  canToggleSettings: boolean;
  muted: boolean;
  onToggleMute: (v: boolean) => void;
  pinned: boolean;
  onTogglePin: (v: boolean) => void;
  /** Caller owns the room → show dismiss (and hide leave). */
  canDismiss: boolean;
  /** Owner of a custom (non-order) group → can rename its title. */
  canRename: boolean;
  onRename: () => void;
  /** Members the caller (room owner) may remove — owner/self/bot excluded.
   * Empty for non-owners, so it also gates the "remove" entry point. */
  removableMembers: ChatRoomActiveMember[];
  /** Owner-kick: remove the given member rows (batch). */
  onRemoveMembers: (memberRowIds: string[]) => void;
  onLeave: () => void;
  onDismiss: () => void;
  /** The room has been dismissed — read-only; only deleting the thread remains. */
  dismissed: boolean;
  onDeleteThread: () => void;
  /** A leave/dismiss/delete request is in flight. */
  busy: boolean;
  onAddPress: () => void;
  /** The caller can view this group but isn't a member yet — show the
   * "join" CTA instead of the member-only actions. */
  canJoin: boolean;
  /** Self-join the room, then reveal it as a member (reloads the detail). */
  onJoin: () => void;
}

export function useRoomDetail(
  handle: ChatConversationHandle,
  onAddMembers: (params: ChatContactPickerParams) => void,
  /** Called after a successful leave/dismiss — host pops back to the inbox. */
  onExit?: () => void,
  /** Called after a successful self-join — host opens the conversation ("show
   * the room"). Falls back to an in-place reload when not provided. */
  onJoined?: () => void,
): RoomDetail {
  const { chatService } = useServiceFactory();
  const { me } = useChatSession();
  // Cross-platform confirm/prompt (RN's Alert.alert/Alert.prompt are no-ops on
  // web; these push a confirm/prompt screen there).
  const confirm = useConfirm();
  const prompt = usePrompt();

  const [room, setRoom] = useState<ChatRoomDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [muted, setMuted] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Once we leave/dismiss, the room (or our membership) is gone. Suppress the
  // focus-effect refetch so a re-focus during teardown can't 404 the dead room
  // and flash the synthesized fallback.
  const exitedRef = useRef(false);

  const myPlatformUserId = me.platformUserId;

  // A `baked` handle is an existing room (load by id); a `bare` handle is a
  // not-yet-created private chat (render a phantom from the peer).
  const roomId = handle.kind === 'baked' ? handle.roomId : null;
  const peer = handle.kind === 'bare' ? handle.peer : null;

  const loadData = useCallback(async () => {
    if (exitedRef.current) return;
    try {
      setLoading(true);
      const contactList = await chatService.listContacts();
      const contactMap = new Map(
        contactList.map(c => [String(c.platformUserId), c]),
      );
      // The viewer is always present; add their own contact entry from the
      // session identity so member rows enrich uniformly.
      contactMap.set(String(myPlatformUserId), {
        memberId: me.user.memberId,
        name: me.user.name,
        avatar: me.user.avatar ?? null,
        jobTitle: me.user.jobTitle,
        role: me.user.role,
        mobile: me.user.mobile ?? null,
        platformUserId: myPlatformUserId,
        appId: me.app.appId,
        appScope: me.app.scope,
      });

      const enrichMembers = (
        members: ChatRoomActiveMember[],
      ): ChatRoomActiveMember[] =>
        members.map(m => {
          const c = contactMap.get(String(m.platformUserId));
          return {
            ...m,
            // The /active-members endpoint sends no row id — fall back to
            // the (stable, unique-per-room) platform user id so list keys
            // don't collide.
            id: m.id ?? String(m.platformUserId),
            displayName: m.name || m.displayName || c?.name || '?',
            avatarUrl: m.avatarUrl ?? c?.avatar ?? null,
          };
        });

      // Phantom mode: no room id yet — synthesize me + the counterpart so
      // the detail UI can render with disabled settings. No round-trip.
      if (!roomId) {
        const selfMember: ChatRoomActiveMember = {
          id: 'phantom-me',
          platformUserId: myPlatformUserId,
          appId: me.app.appId,
          appScope: me.app.scope,
          displayName: me.user.name,
          avatarUrl: me.user.avatar ?? null,
          joinedAt: '',
          leftAt: null,
        };
        const counterpartMembers: ChatRoomActiveMember[] = [];
        if (peer) {
          counterpartMembers.push({
            id: 'phantom-counterpart',
            platformUserId: peer.platformUserId,
            appId: peer.appId,
            appScope: peer.appScope,
            displayName:
              peer.name ||
              contactMap.get(String(peer.platformUserId))?.name ||
              '?',
            avatarUrl: peer.avatar ?? null,
            joinedAt: '',
            leftAt: null,
          });
        }
        setRoom({
          id: '',
          type: 'private',
          groupChatType: null,
          title: null,
          avatarUrl: null,
          customerName: null,
          licensePlateNo: null,
          orderNo: null,
          vin: null,
          ownerUserId: null,
          ownerAppId: null,
          ownerAppScope: null,
          dismissedAt: null,
          createdAt: '',
          members: enrichMembers([selfMember, ...counterpartMembers]),
        });
        return;
      }

      try {
        const detail = await chatService.getRoom(roomId);
        detail.members = sortRoomMembers(enrichMembers(detail.members), detail);
        setRoom(detail);
        setThreadId(detail.threadId ?? null);
        setMuted(!!detail.muted);
        setPinned(!!detail.pinned);
      } catch (e) {
        console.error('getRoom failed, falling back to active members', e);
        const members = await chatService.getRoomActiveMembers(roomId);
        setThreadId(null);
        setRoom({
          id: roomId,
          type: 'private',
          groupChatType: null,
          title: null,
          avatarUrl: null,
          customerName: null,
          licensePlateNo: null,
          orderNo: null,
          vin: null,
          ownerUserId: null,
          ownerAppId: null,
          ownerAppScope: null,
          dismissedAt: null,
          createdAt: '',
          members: enrichMembers(members),
        });
      }
    } catch (e) {
      console.error('load room failed', e);
    } finally {
      setLoading(false);
    }
  }, [chatService, roomId, peer, myPlatformUserId, me]);

  // `useFocusEffect` fires on initial mount AND on every return-from-stack
  // — covers first paint AND the refresh after returning from the contact
  // picker, without the useEffect+useFocusEffect double-trigger race.
  useFocusEffect(
    useCallback(() => {
      void loadData();
    }, [loadData]),
  );

  const isPhantom = handle.kind === 'bare';

  const onAddPress = useCallback(() => {
    if (!room) return;
    const memberPlatformUserIds = room.members.map(m =>
      String(m.platformUserId),
    );
    // Phantom-or-private: upgrade into a group room. Seed the picker with
    // the counterpart so the new group includes them by default.
    if (isPhantom || room.type === 'private') {
      const counterpart = room.members.find(
        m => !idEquals(m.platformUserId, myPlatformUserId),
      );
      onAddMembers({
        mode: {
          kind: 'new-group',
          seedPlatformUserId: counterpart
            ? String(counterpart.platformUserId)
            : undefined,
        },
      });
      return;
    }
    // Existing group room: bulk-invite mode.
    if (!roomId) return;
    onAddMembers({
      mode: {
        kind: 'invite',
        roomId,
        existingMemberPlatformUserIds: memberPlatformUserIds,
      },
    });
  }, [onAddMembers, roomId, room, myPlatformUserId, isPhantom]);

  const isGroup = !isPhantom && room?.type === 'group';
  const dismissed = room?.dismissedAt != null;
  // A dismissed room is read-only: mute/pin/leave/dismiss no longer apply, only
  // deleting the local thread remains.
  const canToggleSettings = !!threadId && !isPhantom && !dismissed;
  // Owner sees dismiss; everyone else sees leave. Gating dismiss to the owner
  // also sidesteps the owner-self-leave orphan case (owner dismisses instead).
  // Owner identity is the full (platformUserId, appId, appScope) triplet —
  // match all three (the numeric id alone can collide across app scopes).
  const canDismiss =
    !!isGroup &&
    room?.ownerUserId != null &&
    idEquals(room.ownerUserId, myPlatformUserId) &&
    room.ownerAppId === me.app.appId &&
    room.ownerAppScope === me.app.scope;

  // Optimistic toggle: flip locally, persist, revert on failure. Pin/mute are
  // per-viewer thread flags, so they go to /threads/:id/{pin,mute}.
  const onToggleMute = useCallback(
    (v: boolean) => {
      if (!threadId) return;
      setMuted(v);
      void chatService.muteThread(threadId, v).catch(e => {
        console.error('muteThread failed', e);
        // Only revert if the user hasn't since toggled again.
        setMuted(curr => (curr === v ? !v : curr));
      });
    },
    [chatService, threadId],
  );
  const onTogglePin = useCallback(
    (v: boolean) => {
      if (!threadId) return;
      setPinned(v);
      void chatService.pinThread(threadId, v).catch(e => {
        console.error('pinThread failed', e);
        setPinned(curr => (curr === v ? !v : curr));
      });
    },
    [chatService, threadId],
  );

  const onLeave = useCallback(async () => {
    if (!roomId || busy) return;
    const ok = await confirm({
      title: '退出群聊',
      message: '退出后将不再接收该群消息，确定退出？',
      confirmButtonText: '退出',
      destructive: true,
    });
    if (!ok) return;
    try {
      setBusy(true);
      await chatService.leaveRoom(roomId);
      exitedRef.current = true;
      onExit?.();
    } catch (e) {
      console.error('leaveRoom failed', e);
    } finally {
      setBusy(false);
    }
  }, [confirm, roomId, busy, chatService, onExit]);

  const onDismiss = useCallback(async () => {
    if (!roomId || busy) return;
    const ok = await confirm({
      title: '解散群聊',
      message: '解散后该群将对所有成员关闭，确定解散？',
      confirmButtonText: '解散',
      destructive: true,
    });
    if (!ok) return;
    try {
      setBusy(true);
      await chatService.dismissRoom(roomId);
      exitedRef.current = true;
      onExit?.();
    } catch (e) {
      console.error('dismissRoom failed', e);
    } finally {
      setBusy(false);
    }
  }, [confirm, roomId, busy, chatService, onExit]);

  // Identity match against the (platformUserId, appId, appScope) triple — the
  // numeric id alone can collide across app scopes.
  const sameIdentity = useCallback(
    (
      m: { platformUserId: IdType; appId: string; appScope: string },
      pid: IdType,
      appId: string | null | undefined,
      appScope: string | null | undefined,
    ) =>
      idEquals(m.platformUserId, pid) &&
      m.appId === appId &&
      m.appScope === appScope,
    [],
  );

  // Owner of a custom group can rename it. Order-internal rooms are excluded:
  // their title is system-composed (`[plate] 内部交流群`) and re-synced on order
  // changes, so a manual rename would be clobbered.
  const canRename = canDismiss && !dismissed && room?.orderNo == null;

  // Rename the group. Empty input clears the custom title → backend restores the
  // member-name default. Optimistic; reload to revert on failure.
  const onRename = useCallback(async () => {
    if (!roomId || busy) return;
    const next = await prompt({
      title: '修改群聊名称',
      message: '留空将恢复为默认的成员名称',
      defaultValue: room?.title ?? '',
      confirmButtonText: '保存',
    });
    if (next == null) return; // cancelled
    const value = next.trim().length ? next.trim() : null;
    if ((room?.title ?? null) === value) return; // unchanged
    // Optimistic for instant feedback; reconcile to the server's canonical
    // title afterwards (covers the clear→member-name-default case, and the
    // web prompt being a pushed screen whose dismissal re-fires loadData).
    setRoom(prev => (prev ? { ...prev, title: value } : prev));
    try {
      setBusy(true);
      await chatService.updateRoomMetadata(roomId, { title: value });
    } catch (e) {
      console.error('rename room failed', e);
    } finally {
      setBusy(false);
      void loadData();
    }
  }, [roomId, busy, prompt, room, chatService, loadData]);

  // Owner-kick is allowed for any active human member that isn't the owner or
  // the caller (and never the bot). Requires the loaded room's owner identity,
  // so it's empty in the active-members fallback (no ownerUserId there) and for
  // non-owners — which also hides the "remove" entry point.
  const removableMembers = useMemo(() => {
    if (!canDismiss || dismissed || room == null) return [];
    return room.members.filter(m => {
      if (m.type === 'bot') return false;
      if (sameIdentity(m, myPlatformUserId, me.app.appId, me.app.scope)) {
        return false;
      }
      if (
        room.ownerUserId != null &&
        sameIdentity(m, room.ownerUserId, room.ownerAppId, room.ownerAppScope)
      ) {
        return false;
      }
      return true;
    });
  }, [canDismiss, dismissed, room, sameIdentity, myPlatformUserId, me]);

  // Batch owner-kick. The backend kick endpoint is per-member; fire them
  // together, then optimistically drop the removed rows. A partial failure
  // falls back to a reload so the grid reflects the canonical membership.
  const onRemoveMembers = useCallback(
    async (memberRowIds: string[]) => {
      if (!roomId || busy || memberRowIds.length === 0) return;
      const idSet = new Set(memberRowIds);
      try {
        setBusy(true);
        await Promise.all(
          memberRowIds.map(id => chatService.kickRoomMember(roomId, id)),
        );
        setRoom(prev =>
          prev
            ? { ...prev, members: prev.members.filter(m => !idSet.has(m.id)) }
            : prev,
        );
      } catch (e) {
        console.error('remove members failed', e);
        void loadData();
      } finally {
        setBusy(false);
      }
    },
    [roomId, busy, chatService, loadData],
  );

  // A same-store non-member viewing the detail can self-join. Only for a
  // live group the caller isn't already in (the backend stamps
  // `viewerIsMember=false`); phantom/private/fallback rooms leave it
  // undefined → no join CTA.
  const canJoin = !!isGroup && !dismissed && room?.viewerIsMember === false;

  const onJoin = useCallback(async () => {
    if (!roomId || busy) return;
    try {
      setBusy(true);
      await chatService.joinRoom(roomId);
      // Joined: hand off to the host to open the conversation ("show the
      // room"). With no host callback (e.g. the desktop info column), reload
      // in place — viewerIsMember flips and the join system message
      // materialises the thread, so the detail becomes a member view.
      if (onJoined) onJoined();
      else await loadData();
    } catch (e) {
      console.error('joinRoom failed', e);
    } finally {
      setBusy(false);
    }
  }, [roomId, busy, chatService, loadData, onJoined]);

  // Delete the viewer's thread entirely (the only action left on a dismissed
  // room). Mirrors the inbox swipe-delete, then exits back to the list.
  const onDeleteThread = useCallback(async () => {
    if (!threadId || busy) return;
    const ok = await confirm({
      title: '删除聊天',
      message: '将删除该会话及本地聊天记录，确定删除？',
      confirmButtonText: '删除',
      destructive: true,
    });
    if (!ok) return;
    try {
      setBusy(true);
      await chatService.deleteThread(threadId);
      exitedRef.current = true;
      onExit?.();
    } catch (e) {
      console.error('deleteThread failed', e);
    } finally {
      setBusy(false);
    }
  }, [confirm, threadId, busy, chatService, onExit]);

  return {
    room,
    loading,
    isPhantom,
    isGroup,
    canToggleSettings,
    muted,
    onToggleMute,
    pinned,
    onTogglePin,
    canDismiss,
    canRename,
    onRename,
    removableMembers,
    onRemoveMembers,
    onLeave,
    onDismiss,
    dismissed,
    onDeleteThread,
    busy,
    onAddPress,
    canJoin,
    onJoin,
  };
}
